import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styles from "./styles/AssetEvaluation.module.css";

/*
  A dropdown that always opens below its field.

  A native <select> on macOS is a popup menu: it positions the list so the
  chosen row lands on top of the closed control, so the panel appears in a
  different place depending on what is selected. That is not something CSS can
  change — the only fix is to stop using a native select.

  The menu is rendered into document.body rather than next to the trigger,
  because the modal body (`.popup_scrollable`) is an `overflow: auto` container
  and would clip it. It is positioned from the trigger's rect and follows it on
  scroll and resize. It flips above the field only when there is genuinely no
  room below.

  `groups`: [{ label, options: [{ value, label }] }]
  Group labels are headings, never selectable — the same contract as <optgroup>.
*/

const MAX_MENU_HEIGHT = 260;
const MAX_MENU_WIDTH = 380;
// Floor for the squeezed case where the menu fits neither above nor below.
const MIN_MENU_HEIGHT = 80;
const GAP = 4;
// Breathing room kept between the menu and the edge of the viewport.
const MARGIN = 8;

const Dropdown = React.forwardRef(function Dropdown({
  value,
  onChange,
  groups = [],
  placeholder = "",
  disabled = false,
  className = "",
  ariaLabel,
  title,
  /*
    How tall the open list may get before it scrolls. Defaults to the cap this
    panel uses; a host with different room to work in passes its own.
  */
  maxMenuHeight = MAX_MENU_HEIGHT,
  /*
    Replaces the trigger's own styling rather than adding to it, so the control
    can wear the host's field style instead of this panel's. Given one, the
    caller owns the closed control entirely.
  */
  triggerClassName,
}, ref) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const triggerRef = useRef(null);
  /*
    The trigger is the focusable element, so a forwarded ref points at it —
    that is what lets a host focus this control the way it would a <select>.
    Merged rather than replaced, because the positioning below measures the
    same node.
  */
  const setTriggerRef = (el) => {
    triggerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };
  const menuRef = useRef(null);
  const activeRef = useRef(null);
  // The menu's unconstrained height, measured once each time it opens.
  const contentHeightRef = useRef(null);

  const flat = groups.flatMap((group) => group.options);
  const selected = flat.find((option) => option.value === value) || null;

  const position = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN;
    const spaceAbove = rect.top - GAP - MARGIN;

    /*
      How tall this menu actually wants to be — its own content, capped. The
      first pass after opening has not measured it yet and assumes the cap; the
      layout effect below then measures and re-runs this before paint.
    */
    const wanted = Math.min(
      contentHeightRef.current == null
        ? maxMenuHeight
        : contentHeightRef.current,
      maxMenuHeight
    );

    /*
      Below whenever the whole menu fits there — that is the normal case, and a
      short list opens below even low on the screen. Only when it does not fit
      below does the side above get considered, and only if the menu fits there.
      If neither side can hold it, take the roomier one and let it scroll.
    */
    let below;
    if (spaceBelow >= wanted) below = true;
    else if (spaceAbove >= wanted) below = false;
    else below = spaceBelow >= spaceAbove;

    const available = below ? spaceBelow : spaceAbove;
    setBox({
      left: rect.left,
      width: rect.width,
      /*
        At least as wide as the field, but free to grow so the long debt types
        read on one line — the trigger truncates them, the list should not.
        Clamped to what is left of the viewport so it never runs off the edge.
      */
      maxWidth: Math.max(
        rect.width,
        Math.min(MAX_MENU_WIDTH, window.innerWidth - rect.left - MARGIN)
      ),
      top: below ? rect.bottom + GAP : undefined,
      // Anchoring upwards by `bottom` avoids having to measure the menu first.
      bottom: below ? undefined : window.innerHeight - rect.top + GAP,
      maxHeight: Math.max(MIN_MENU_HEIGHT, Math.min(wanted, available)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxMenuHeight]);

  useLayoutEffect(() => {
    if (!open) {
      contentHeightRef.current = null;
      return;
    }
    position();
  }, [open, position]);

  /*
    The menu does not exist on the render that opens it, so its height can only
    be measured on the next one. Measure once per opening and re-position with
    the real number — in a layout effect, so the correction lands before paint
    and the menu never appears on the wrong side.
  */
  useLayoutEffect(() => {
    if (!open || !menuRef.current || contentHeightRef.current != null) return;
    contentHeightRef.current = menuRef.current.scrollHeight;
    position();
  }, [open, box, position]);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => position();
    // Capture phase, so scrolling the modal body counts and not just the page.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    const onPointerDown = (event) => {
      if (triggerRef.current && triggerRef.current.contains(event.target)) return;
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, position]);

  /*
    Keep the highlighted row in view by scrolling the menu's own overflow.
    `scrollIntoView` would be shorter but is allowed to scroll ancestors too,
    and nudging the modal behind the menu is exactly the kind of jump this
    component exists to avoid.
  */
  useEffect(() => {
    const menu = menuRef.current;
    const row = activeRef.current;
    if (!open || !menu || !row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < menu.scrollTop) menu.scrollTop = top;
    else if (bottom > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = bottom - menu.clientHeight;
    }
    // `box` matters: the menu does not exist on the render that opens it, only
    // on the one after `position()` has measured the trigger.
  }, [open, activeIndex, box]);

  const openMenu = () => {
    if (disabled) return;
    const index = selected ? flat.indexOf(selected) : 0;
    setActiveIndex(index < 0 ? 0 : index);
    setOpen(true);
  };

  const choose = (option) => {
    onChange(option.value);
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  const onKeyDown = (event) => {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        openMenu();
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (flat[activeIndex]) choose(flat[activeIndex]);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((prev) => {
        const next = prev + step;
        if (next < 0) return 0;
        if (next > flat.length - 1) return flat.length - 1;
        return next;
      });
    }
  };

  return (
    <div className={styles.dd_wrap}>
      <button
        type="button"
        ref={setTriggerRef}
        /*
          An option whose value is empty is the "nothing chosen" row, not a
          choice — it greys like the placeholder it stands in for. Lists without
          such a row (the debt type) are unaffected.
        */
        className={`${
          triggerClassName || `${styles.select} ${styles.dd_trigger}`
        } ${
          selected && selected.value !== "" ? "" : styles.select_placeholder
        } ${className}`}
        disabled={disabled}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        {selected ? selected.label : placeholder}
      </button>

      {open &&
        box &&
        createPortal(
          <div
            ref={menuRef}
            /*
              The menu is portaled into document.body, so a host watching for a
              click outside itself — the Asset Management row lock — counts a
              click on an option as a click away. This marks it as part of the
              control that opened it; that check is `closest("[data-dropdown-menu]")`.
            */
            data-dropdown-menu="true"
            className={styles.dd_menu}
            role="listbox"
            aria-label={ariaLabel}
            style={{
              left: box.left,
              minWidth: box.width,
              maxWidth: box.maxWidth,
              top: box.top,
              bottom: box.bottom,
              maxHeight: box.maxHeight,
            }}
          >
            {groups.map((group, groupIndex) => (
              <div key={group.label || groupIndex}>
                {group.label ? (
                  <div className={styles.dd_group_label} role="presentation">
                    {group.label}
                  </div>
                ) : null}
                {group.options.map((option) => {
                  const index = flat.indexOf(option);
                  const isActive = index === activeIndex;
                  return (
                    <div
                      key={option.value}
                      role="option"
                      aria-selected={option.value === value}
                      ref={isActive ? activeRef : undefined}
                      className={`${styles.dd_option} ${
                        option.value === value ? styles.dd_option_selected : ""
                      } ${isActive ? styles.dd_option_active : ""}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      /* Keeps focus on the trigger, so the menu never closes
                         from a blur before the click lands. */
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => choose(option)}
                    >
                      {option.label}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
});

export default Dropdown;
